import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import {
    MdAddChart,
    MdAccountBalanceWallet,
    MdWarningAmber,
    MdTrendingUp,
    MdCalendarMonth,
    MdCheckCircle,
    MdInfoOutline,
} from "react-icons/md";
import { BASE_API_URL } from "../helpers/Config";
import { TriggerBalanceUpdate } from "../helpers/TriggerBalanceUpdate";
import { getPageStyles, MOTION } from "../helpers/pageTheme";

const HERO_GRADIENT =
    "linear-gradient(135deg, #059669 0%, #047857 55%, #065f46 100%)";

const SUBMIT_BTN = {
    borderRadius: "12px",
    padding: "14px",
    fontWeight: 700,
    fontSize: "1rem",
    border: "none",
    background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
    color: "#fff",
    boxShadow: "0 6px 20px rgba(5, 150, 105, 0.4)",
};

const PLAN_STYLES = {
    lite: { accent: "#94a3b8", label: "Lite" },
    plus: { accent: "#3b82f6", label: "Plus" },
    pro: { accent: "#8b5cf6", label: "Pro" },
    max: { accent: "#f59e0b", label: "Max" },
};

const getApiError = (payload) => {
    if (!payload) return "Invalid response";
    if (payload.error === "session expired") return "session expired";
    if (payload.error === false || payload.error === null || payload.error === undefined) {
        return null;
    }
    if (payload.error === true) return payload?.message || "Request failed";
    if (typeof payload.error === "string") return payload.error;
    return null;
};

const parseSelfPlans = (data) => {
    if (data && typeof data === "object" && Array.isArray(data.self)) {
        return [...data.self];
    }
    return [];
};

const formatAmount = (value) => {
    const n = Number(value);
    if (isNaN(n)) return "$0";
    return `$${n.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    })}`;
};

const findPlanForAmount = (amount, plans) => {
    if (!amount || amount <= 0 || !plans?.length) return null;
    return (
        plans.find((p) => {
            const from = Number(p.from);
            const to = Number(p.to);
            return amount >= from && amount <= to;
        }) || null
    );
};

const getAmountValidation = (amount, balance, plans, loadingPlans) => {
    if (loadingPlans) {
        return { valid: false, reason: "Loading commission plans…", plan: null };
    }
    if (!plans?.length) {
        return { valid: false, reason: "Commission plans unavailable", plan: null };
    }
    if (!amount || amount <= 0) {
        return { valid: false, reason: "Enter an investment amount", plan: null };
    }
    if (amount > balance) {
        return { valid: false, reason: "Insufficient USDT balance", plan: null };
    }
    const plan = findPlanForAmount(amount, plans);
    if (!plan) {
        const min = Math.min(...plans.map((p) => Number(p.from)));
        const max = Math.max(...plans.map((p) => Number(p.to)));
        return {
            valid: false,
            reason: `Min ${formatAmount(min)} & Max: ${formatAmount(max)} amount allowed`,
            plan: null,
        };
    }
    return { valid: true, reason: null, plan };
};

const PlanBadge = ({ planName }) => {
    const key = (planName || "").toLowerCase();
    const cfg = PLAN_STYLES[key] || { accent: "#059669", label: planName || "—" };
    return (
        <span
            className="badge rounded-pill text-uppercase"
            style={{
                fontSize: "0.68rem",
                fontWeight: 700,
                backgroundColor: `${cfg.accent}22`,
                color: cfg.accent,
                border: `1px solid ${cfg.accent}55`,
            }}
        >
            {cfg.label}
        </span>
    );
};

const SectionCard = ({ styles, children }) => (
    <div className="p-3" style={styles.card}>
        {children}
    </div>
);

const CreateRecoveryTrade = ({ theme = "dark" }) => {
    const [amount, setAmount] = useState("");
    const [usdtBalance, setUsdtBalance] = useState(0);
    const [selfPlans, setSelfPlans] = useState([]);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loadingBalance, setLoadingBalance] = useState(true);
    const [loadingPlans, setLoadingPlans] = useState(true);

    const navigate = useNavigate();
    const isDark = theme === "dark";
    const styles = useMemo(() => getPageStyles(isDark), [isDark]);

    const maxChip = useMemo(
        () => ({
            borderRadius: "8px",
            padding: "6px 12px",
            fontSize: "0.75rem",
            fontWeight: 700,
            border: `1px solid ${isDark ? "#059669" : "#6ee7b7"}`,
            backgroundColor: isDark ? "rgba(5, 150, 105, 0.15)" : "#d1fae5",
            color: isDark ? "#6ee7b7" : "#047857",
        }),
        [isDark]
    );

    const fetchProfile = useCallback(async () => {
        setLoadingBalance(true);
        try {
            const response = await axios.post(`${BASE_API_URL}/account/profile`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
            });
            const data = response?.data;

            if (data?.error) {
                if (data?.error === "session expired") {
                    localStorage.removeItem("a5srf4wer7w1cd");
                    navigate("/login");
                    return;
                }
                toast.error(data?.error);
                return;
            }

            if (data?.data) {
                setUsdtBalance(parseFloat(data.data.usdt) || 0);
                TriggerBalanceUpdate({
                    usdt: data.data.usdt,
                    balance: data.data.balance,
                });
            }
        } catch {
            toast.error("Failed to load balance");
        } finally {
            setLoadingBalance(false);
        }
    }, [navigate]);

    const fetchCommissionPlans = useCallback(async () => {
        setLoadingPlans(true);
        try {
            const response = await axios.post(
                `${BASE_API_URL}/recovery-trade/commission-chart`,
                { token: localStorage.getItem("a5srf4wer7w1cd") }
            );
            const payload = response?.data;
            const apiError = getApiError(payload);

            if (apiError === "session expired") {
                localStorage.removeItem("a5srf4wer7w1cd");
                navigate("/login");
                return;
            }

            if (apiError) {
                toast.error(apiError);
                setSelfPlans([]);
                return;
            }

            setSelfPlans(parseSelfPlans(payload?.data));
        } catch {
            toast.error("Failed to load commission plans");
            setSelfPlans([]);
        } finally {
            setLoadingPlans(false);
        }
    }, [navigate]);

    useEffect(() => {
        fetchProfile();
        fetchCommissionPlans();
    }, [fetchProfile, fetchCommissionPlans]);

    const numericAmount = parseFloat(amount) || 0;

    const validation = useMemo(
        () =>
            getAmountValidation(
                numericAmount,
                usdtBalance,
                selfPlans,
                loadingPlans
            ),
        [numericAmount, usdtBalance, selfPlans, loadingPlans]
    );

    const activePlan = validation.plan;

    const recoveryPreview = useMemo(() => {
        if (!activePlan || numericAmount <= 0) return null;
        const multiplier = Number(activePlan.recovery) || 0;
        const maxMonth = Number(activePlan.max_month) || 0;
        const totalRecovery = numericAmount * multiplier;
        const maxDays = maxMonth > 0 ? maxMonth * 30 : 0;
        return { multiplier, totalRecovery, maxMonth, maxDays };
    }, [activePlan, numericAmount]);

    const handleAmountChange = (e) => {
        const val = e.target.value;
        if (val === "" || /^\d*\.?\d*$/.test(val)) {
            setAmount(val);
        }
    };

    const handleMaxClick = () => {
        if (!selfPlans.length) {
            setAmount(String(Math.floor(usdtBalance)));
            return;
        }
        const maxPlanTo = Math.max(...selfPlans.map((p) => Number(p.to)));
        const capped = Math.min(Math.floor(usdtBalance), maxPlanTo);
        setAmount(String(capped));
    };

    const canSubmit =
        validation.valid && !isSubmitting && !loadingBalance && !loadingPlans;

    const handleOpenConfirm = () => {
        if (!canSubmit) {
            if (validation.reason) toast.error(validation.reason);
            return;
        }
        setShowConfirmModal(true);
    };

    const handleCreate = async () => {
        if (!canSubmit) return;
        setIsSubmitting(true);
        try {
            const response = await axios.post(
                `${BASE_API_URL}/recovery-trade/create`,
                {
                    token: localStorage.getItem("a5srf4wer7w1cd"),
                    amount: numericAmount,
                }
            );
            const data = response?.data;

            if (data?.error) {
                if (data?.error === "session expired") {
                    localStorage.removeItem("a5srf4wer7w1cd");
                    navigate("/login");
                    return;
                }
                toast.error(
                    typeof data.error === "string"
                        ? data.error
                        : "Failed to create recovery trade"
                );
                return;
            }

            toast.success(data?.msg || "Recovery trade created successfully");
            setAmount("");
            setShowConfirmModal(false);
            await fetchProfile();
            navigate("/recovery-trade");
        } catch (err) {
            const msg =
                err.response?.data?.error ||
                err.response?.data?.message ||
                "Failed to create recovery trade";
            toast.error(typeof msg === "string" ? msg : "Network error");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.35 }}
            className="px-3 py-3 overflow-auto"
            style={styles.page}
        >
            <motion.div
                variants={MOTION.container}
                initial="hidden"
                animate="visible"
                className="d-flex flex-column gap-3"
                style={{ maxWidth: 480, margin: "0 auto", paddingBottom: "24px" }}
            >
                <motion.div variants={MOTION.item} style={styles.hero(HERO_GRADIENT)}>
                    <div className="d-flex align-items-center gap-2 mb-3">
                        <MdAddChart size={26} />
                        <div>
                            <h5 className="fw-bold mb-0">New Recovery Trade</h5>
                            <p className="mb-0 small opacity-75">
                                Invest USDT into recovery
                            </p>
                        </div>
                    </div>
                    <div
                        className="d-flex align-items-center justify-content-between py-2 px-3 rounded-3"
                        style={{ background: "rgba(255,255,255,0.12)" }}
                    >
                        <span className="small opacity-75 d-flex align-items-center gap-1">
                            <MdAccountBalanceWallet size={18} />
                            Available USDT
                        </span>
                        <span className="fw-bold">
                            {loadingBalance ? "…" : formatAmount(usdtBalance)}
                        </span>
                    </div>
                </motion.div>

                <motion.div variants={MOTION.item}>
                    <SectionCard styles={styles}>
                        {loadingPlans ? (
                            <div className="text-center py-3">
                                <div
                                    className="spinner-border spinner-border-sm text-success"
                                    role="status"
                                />
                            </div>
                        ) : selfPlans.length === 0 ? (
                            <p className="mb-0 small" style={{ color: styles.textMuted }}>
                                No plans available. Pull to refresh by revisiting this page.
                            </p>
                        ) : (
                            <div className="d-flex flex-column gap-2">
                                {selfPlans.map((plan, index) => {
                                    const isActive =
                                        activePlan?.plan === plan.plan &&
                                        numericAmount > 0;
                                    const accent =
                                        PLAN_STYLES[(plan.plan || "").toLowerCase()]
                                            ?.accent || "#059669";
                                    return (
                                        <div
                                            key={`${plan.plan}-${index}`}
                                            className="p-2 rounded-3"
                                            style={{
                                                border: `1px solid ${isActive
                                                    ? accent
                                                    : isDark
                                                        ? "#1f2937"
                                                        : "#e5e7eb"
                                                    }`,
                                                backgroundColor: isActive
                                                    ? isDark
                                                        ? `${accent}18`
                                                        : `${accent}10`
                                                    : isDark
                                                        ? "#0f172a"
                                                        : "#f8fafc",
                                            }}
                                        >
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <PlanBadge planName={plan.plan} />
                                                {isActive && (
                                                    <MdCheckCircle
                                                        size={18}
                                                        style={{ color: accent }}
                                                    />
                                                )}
                                            </div>
                                            <p
                                                className="mb-1 small fw-semibold"
                                                style={{ fontSize: "0.8rem" }}
                                            >
                                                {formatAmount(plan.from)} –{" "}
                                                {formatAmount(plan.to)}
                                            </p>
                                            <p
                                                className="mb-0 small"
                                                style={{ color: styles.textMuted }}
                                            >
                                                {plan.recovery}x recovery · up to{" "}
                                                {plan.max_month} months (
                                                {Number(plan.max_month) * 30} days)
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </SectionCard>
                </motion.div>

                <motion.div variants={MOTION.item}>
                    <SectionCard styles={styles}>
                        <p className="mb-2" style={styles.sectionTitle}>
                            Investment amount
                        </p>
                        <div
                            className="d-flex align-items-center gap-2 mb-2 px-3 py-2"
                            style={{
                                ...styles.input,
                                minHeight: "52px",
                                borderColor: !validation.valid && numericAmount > 0
                                    ? isDark
                                        ? "#f87171"
                                        : "#fca5a5"
                                    : styles.input.border,
                            }}
                        >
                            <span
                                className="fw-bold flex-shrink-0"
                                style={{ fontSize: "1.2rem", color: "#059669" }}
                            >
                                $
                            </span>
                            <input
                                type="text"
                                inputMode="decimal"
                                className="form-control border-0 shadow-none flex-grow-1 fw-bold"
                                placeholder="0.00"
                                value={amount}
                                onChange={handleAmountChange}
                                disabled={loadingPlans || loadingBalance}
                                style={{
                                    fontSize: "1.25rem",
                                    outline: "none",
                                    backgroundColor: "transparent",
                                    color: isDark ? "#f9fafb" : "#111827",
                                    minWidth: 0,
                                }}
                            />
                            <motion.button
                                type="button"
                                className="btn flex-shrink-0"
                                style={maxChip}
                                whileTap={{ scale: 0.96 }}
                                onClick={handleMaxClick}
                                disabled={loadingBalance || loadingPlans}
                            >
                                MAX
                            </motion.button>
                        </div>

                        {numericAmount > 0 && !validation.valid && validation.reason && (
                            <p
                                className="mb-2 small d-flex align-items-start gap-1"
                                style={{ color: isDark ? "#f87171" : "#dc2626" }}
                            >
                                <MdInfoOutline size={16} className="flex-shrink-0 mt-1" />
                                {validation.reason}
                            </p>
                        )}

                        {recoveryPreview && (
                            <div
                                className="p-3 rounded-3 mb-3"
                                style={{
                                    backgroundColor: isDark
                                        ? "rgba(5, 150, 105, 0.15)"
                                        : "#ecfdf5",
                                    border: `1px solid ${isDark ? "rgba(52, 211, 153, 0.35)" : "#a7f3d0"}`,
                                }}
                            >
                                <p
                                    className="mb-2 text-uppercase"
                                    style={{
                                        fontSize: "0.65rem",
                                        fontWeight: 700,
                                        letterSpacing: "0.06em",
                                        color: isDark ? "#6ee7b7" : "#047857",
                                    }}
                                >
                                    Your recovery preview
                                </p>
                                <div className="d-flex align-items-center gap-2 mb-2">
                                    <MdTrendingUp
                                        size={22}
                                        style={{ color: "#059669" }}
                                    />
                                    <div>
                                        <p className="mb-0 fw-bold fs-5 text-success">
                                            {formatAmount(recoveryPreview.totalRecovery)}
                                        </p>
                                        <p
                                            className="mb-0 small"
                                            style={{ color: styles.textMuted }}
                                        >
                                            Total recovery ({recoveryPreview.multiplier}x
                                            of {formatAmount(numericAmount)} invested)
                                        </p>
                                    </div>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <MdCalendarMonth
                                        size={20}
                                        style={{ color: "#059669" }}
                                    />
                                    <p className="mb-0 small">
                                        Guaranteed full recovery within{" "}
                                        <strong>{recoveryPreview.maxMonth} months</strong>{" "}
                                        ({recoveryPreview.maxDays} days)
                                    </p>
                                </div>
                                {activePlan && (
                                    <p
                                        className="mb-0 mt-2 small"
                                        style={{ color: styles.textMuted }}
                                    >
                                        Plan:{" "}
                                        <PlanBadge planName={activePlan.plan} />
                                    </p>
                                )}
                            </div>
                        )}
                    </SectionCard>
                </motion.div>

                <motion.div variants={MOTION.item}>
                    <div style={styles.notice}>
                        Amount must match a self commission plan tier (min–max range)
                        and not exceed your USDT balance. Recovery multiplier and term
                        depend on the matched plan.
                    </div>
                </motion.div>

                <motion.div variants={MOTION.item}>
                    <motion.button
                        type="button"
                        className="btn w-100"
                        style={{
                            ...SUBMIT_BTN,
                            opacity: canSubmit ? 1 : 0.55,
                            boxShadow: canSubmit ? SUBMIT_BTN.boxShadow : "none",
                            cursor: canSubmit ? "pointer" : "not-allowed",
                        }}
                        whileHover={canSubmit ? { scale: 1.02 } : {}}
                        whileTap={canSubmit ? { scale: 0.98 } : {}}
                        disabled={!canSubmit}
                        onClick={handleOpenConfirm}
                    >
                        Create recovery trade
                    </motion.button>
                </motion.div>
            </motion.div>

            {createPortal(
                <AnimatePresence>
                    {showConfirmModal && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="crt-modal-backdrop"
                            onClick={() => !isSubmitting && setShowConfirmModal(false)}
                        >
                            <motion.div
                                initial={{ scale: 0.92, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.92, opacity: 0 }}
                                className="crt-modal-panel"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div style={{ ...styles.modal, width: "100%" }}>
                                    <div className="modal-header border-0 px-4 pt-4 pb-0">
                                        <h6 className="modal-title fw-bold">
                                            Confirm recovery trade
                                        </h6>
                                        <button
                                            type="button"
                                            className="btn-close"
                                            disabled={isSubmitting}
                                            onClick={() => setShowConfirmModal(false)}
                                            aria-label="Close"
                                        />
                                    </div>
                                    <div className="modal-body px-4 py-3">
                                        {isSubmitting ? (
                                            <div className="text-center py-4">
                                                <div
                                                    className="spinner-border text-success mb-3"
                                                    role="status"
                                                />
                                                <p
                                                    className="mb-0"
                                                    style={{ color: styles.textMuted }}
                                                >
                                                    Creating recovery trade…
                                                </p>
                                            </div>
                                        ) : (
                                            <>
                                                <div
                                                    className="text-center p-3 rounded mb-3"
                                                    style={styles.input}
                                                >
                                                    <div
                                                        className="fs-4 fw-bold"
                                                        style={{ color: "#059669" }}
                                                    >
                                                        {formatAmount(numericAmount)}
                                                    </div>
                                                    <small
                                                        style={{ color: styles.textMuted }}
                                                    >
                                                        USDT investment
                                                        {activePlan && (
                                                            <>
                                                                {" "}
                                                                · <PlanBadge
                                                                    planName={activePlan.plan}
                                                                />
                                                            </>
                                                        )}
                                                    </small>
                                                </div>
                                                {recoveryPreview && (
                                                    <div
                                                        className="p-3 rounded mb-3 small"
                                                        style={{
                                                            backgroundColor: isDark
                                                                ? "rgba(5, 150, 105, 0.12)"
                                                                : "#ecfdf5",
                                                            color: isDark
                                                                ? "#6ee7b7"
                                                                : "#047857",
                                                        }}
                                                    >
                                                        <p className="mb-1">
                                                            <strong>Total recovery:</strong>{" "}
                                                            {formatAmount(
                                                                recoveryPreview.totalRecovery
                                                            )}{" "}
                                                            ({recoveryPreview.multiplier}x)
                                                        </p>
                                                        <p className="mb-0">
                                                            <strong>Guaranteed within:</strong>{" "}
                                                            {recoveryPreview.maxMonth} months (
                                                            {recoveryPreview.maxDays} days)
                                                        </p>
                                                    </div>
                                                )}
                                                <div
                                                    className="d-flex justify-content-between small mb-3"
                                                    style={{ color: styles.textMuted }}
                                                >
                                                    <span>Balance after</span>
                                                    <span className="fw-semibold">
                                                        {formatAmount(
                                                            usdtBalance - numericAmount
                                                        )}
                                                    </span>
                                                </div>
                                                <div
                                                    className="d-flex align-items-start gap-2 p-3 rounded"
                                                    style={{
                                                        backgroundColor: isDark
                                                            ? "rgba(245, 158, 11, 0.12)"
                                                            : "#fffbeb",
                                                        border: `1px solid ${isDark ? "#92400e" : "#fde68a"
                                                            }`,
                                                        fontSize: "0.78rem",
                                                        color: isDark ? "#fcd34d" : "#92400e",
                                                    }}
                                                >
                                                    <MdWarningAmber
                                                        size={20}
                                                        className="flex-shrink-0 mt-1"
                                                    />
                                                    <span>
                                                        This action cannot be undone. Verify the
                                                        amount before confirming.
                                                    </span>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    {!isSubmitting && (
                                        <div className="modal-footer border-0 px-4 pb-4 gap-2 d-flex">
                                            <button
                                                type="button"
                                                className="btn flex-grow-1"
                                                style={styles.outlineBtn}
                                                onClick={() => setShowConfirmModal(false)}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                className="btn flex-grow-1 text-white"
                                                style={SUBMIT_BTN}
                                                onClick={handleCreate}
                                            >
                                                Confirm
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>,
                document.body
            )}

            <style>{`
                .crt-modal-backdrop {
                    position: fixed;
                    inset: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 16px;
                    background-color: rgba(0, 0, 0, 0.55);
                    backdrop-filter: blur(4px);
                    z-index: 10000;
                }
                .crt-modal-panel {
                    width: 100%;
                    max-width: 400px;
                    max-height: 85vh;
                    overflow-y: auto;
                }
            `}</style>
        </motion.div>
    );
};

export default CreateRecoveryTrade;
